import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, fmtReminderWhen, reminderSortKey, todayStr } from "../lib/format";
import { useMcOps } from "../lib/McOpsContext";
import { ReminderForm } from "../components/ReminderForm";
import { ColumnFilter } from "../components/Common";
import { REMINDER_FILTERS, filterReminders } from "../lib/reminderFilter";

const ReminderRow = ({ reminder, onDone, onOpen }) => (
  <div
    className={`reminder-item${reminder.jobId ? " clickable" : ""}`}
    style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 8,
    }}
    onClick={() => reminder.jobId && onOpen(reminder.jobId)}
    title={reminder.jobId ? "Open the job" : undefined}
  >
    <div>
      {reminder.text}
      <div className="meta">
        {reminder.jobLabel ? reminder.jobLabel + " · " : ""}
        {fmtReminderWhen(reminder)}
      </div>
    </div>
    <button
      className="btn small"
      onClick={(e) => {
        e.stopPropagation();
        onDone(reminder.id);
      }}
    >
      <Icon name="check" />
      Done
    </button>
  </div>
);

const Column = ({ label, color, list, renderRow, toolbar }) => (
  <div className="kcol">
    <div className="kcol-accent" style={{ background: color }} />
    <div className="kcol-head">
      <span className="htxt">
        <span className="kcol-dot" style={{ background: color }} />
        {label}
      </span>
      <span className="count">{list.length}</span>
    </div>
    {toolbar ? <div className="kcol-toolbar">{toolbar}</div> : null}
    <div className="kcol-body">
      {list.length ? (
        list.map(renderRow)
      ) : (
        <div className="kcol-empty">None</div>
      )}
    </div>
  </div>
);

/** Open post-move issues of a job, newest first (the order they are stored in). */
const openIssues = (job) =>
  (Array.isArray(job.pmIssues) ? job.pmIssues : []).filter((i) => i.status !== "resolved");

/** The date an issue is filtered by: its reminder date, else the day it was logged. */
const issueDate = (job) => {
  const latest = openIssues(job)[0];
  return latest.remDate || String(latest.date || "").slice(0, 10);
};

const IssueRow = ({ job, onOpen }) => {
  const open = openIssues(job);
  const latest = open[0];
  return (
    <div
      className="reminder-item"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <div>
        {job.clientName} — {job.zohoId}{" "}
        <span className="badge plain mini">
          {job.moveType === "ld" ? "Long Distance" : "Local"}
        </span>
        <div className="meta">{latest.text}</div>
        <div className="meta">
          {open.length} open issue{open.length > 1 ? "s" : ""}
          {latest.remDate ? " · Reminder " + fmtDate(latest.remDate) : ""}
        </div>
      </div>
      <button className="btn small" onClick={() => onOpen(job.id, "s5")}>
        <Icon name="alert" />
        Open
      </button>
    </div>
  );
};

export const RemindersPage = () => {
  const { state, completeReminder, openJob } = useMcOps();
  const todayD = todayStr();
  // One All / Today / This week filter per column; Due Today is a single day.
  const [filters, setFilters] = useState({ overdue: "all", upcoming: "all", callback: "all", issues: "all" });
  const filterFor = (column) => (
    <ColumnFilter
      options={REMINDER_FILTERS}
      value={filters[column]}
      onChange={(key) => setFilters((prev) => ({ ...prev, [column]: key }))}
    />
  );

  const active = state.reminders
    .filter((r) => !r.completed)
    .sort((a, b) => reminderSortKey(a).localeCompare(reminderSortKey(b)));
  const overdue = filterReminders(
    active.filter((r) => r.date < todayD),
    filters.overdue,
    "overdue",
    todayD,
  );
  const dueToday = active.filter((r) => r.date === todayD);
  const upcoming = filterReminders(
    active.filter((r) => r.date > todayD),
    filters.upcoming,
    "upcoming",
    todayD,
  );
  const callbacks = filterReminders(
    state.jobs
      .filter((j) => j.callbackDate)
      .sort((a, b) => new Date(a.callbackDate) - new Date(b.callbackDate)),
    filters.callback,
    "callback",
    todayD,
    (j) => j.callbackDate,
  );

  const issueJobs = filterReminders(
    state.jobs.filter((j) => openIssues(j).length),
    filters.issues,
    "callback",
    todayD,
    issueDate,
  );

  return (
    <div className="page active">
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Reminders</h2>
          <div className="page-desc">
            Every reminder across every job — overdue, due today, and upcoming — in
            the same format as the header Set Reminder hub.
          </div>
        </div>
      </div>

      <div className="section-label" style={{ marginTop: 0 }}>
        <Icon name="bell" />
        New Reminder
      </div>
      <ReminderForm style={{ marginBottom: 14 }} />

      <div className="kanban cols-5">
        <Column
          label="Overdue"
          color="var(--danger)"
          list={overdue}
          toolbar={filterFor("overdue")}
          renderRow={(r) => (
            <ReminderRow reminder={r} onDone={completeReminder} onOpen={openJob} key={r.id} />
          )}
        />
        <Column
          label="Due Today"
          color="var(--warning)"
          list={dueToday}
          renderRow={(r) => (
            <ReminderRow reminder={r} onDone={completeReminder} onOpen={openJob} key={r.id} />
          )}
        />
        <Column
          label="Upcoming"
          color="var(--primary)"
          list={upcoming}
          toolbar={filterFor("upcoming")}
          renderRow={(r) => (
            <ReminderRow reminder={r} onDone={completeReminder} onOpen={openJob} key={r.id} />
          )}
        />
        <Column
          label="Callback"
          color="var(--purple)"
          list={callbacks}
          toolbar={filterFor("callback")}
          renderRow={(j) => (
            <div
              className="reminder-item"
              key={j.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div>
                {j.clientName} — {j.zohoId}
                <div className="meta">
                  {fmtDate(j.callbackDate)}
                  {j.callbackTime ? " · " + j.callbackTime : ""}
                </div>
              </div>
              <button className="btn small" onClick={() => openJob(j.id, "s2")}>
                <Icon name="phone" />
                Open
              </button>
            </div>
          )}
        />
        <Column
          label="Post Move Ongoing Issues"
          color="var(--danger)"
          list={issueJobs}
          toolbar={filterFor("issues")}
          renderRow={(j) => <IssueRow job={j} onOpen={openJob} key={j.id} />}
        />
      </div>
    </div>
  );
};
